import {Box, ButtonBase, Typography} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid';
import {ReactNode} from 'react';
import {ServiceRequestDashboardChildRow, ServiceRequestDashboardRow} from '../types/service-requests-dashboard.type';
import {ServiceRequestStatusColorCircle} from '../../../../ServiceRequests/ui/ServiceRequestStatusColorCircle';
import dayjs from 'dayjs';
import {DATE_FORMAT} from '../../../../../shared/constants';
import {StatusColorCell} from '../ui/StatusColorCell';

const PARENT_ROW_HEIGHT = 40;
const CHILD_ROW_HEIGHT = 40;
const DIVIDER_COLOR = '#E5E7EB';

const parentCellStyles = {
  minHeight: PARENT_ROW_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
};

const placeholderRowStyles = {
  minHeight: CHILD_ROW_HEIGHT,
  borderTop: `1px solid ${DIVIDER_COLOR}`,
};

const childRowStyles = {
  minHeight: CHILD_ROW_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  borderTop: `1px solid ${DIVIDER_COLOR}`,
};

const textStyles = {
  fontSize: 12,
  lineHeight: '16px',
};

const renderText = (value?: string) =>
  value ? (
    <Typography noWrap title={value} sx={textStyles}>
      {value}
    </Typography>
  ) : null;

const renderParentColumn = (
  value: ReactNode,
  childCount: number,
  isOpen: boolean,
) => (
  <Box sx={{width: '100%'}}>
    <Box sx={parentCellStyles}>{value}</Box>
    {isOpen &&
      Array.from({length: childCount}).map((_, index) => (
        <Box key={index} sx={placeholderRowStyles} />
      ))}
  </Box>
);

const renderChildrenColumn = (
  children: ServiceRequestDashboardChildRow[],
  isOpen: boolean,
  renderChild: (child: ServiceRequestDashboardChildRow) => ReactNode,
  renderParent?: () => ReactNode,
) => (
  <Box sx={{width: '100%'}}>
    <Box sx={parentCellStyles}>{renderParent?.()}</Box>
    {isOpen &&
      children.map((child) => (
        <Box key={child.id} sx={childRowStyles}>
          {renderChild(child)}
        </Box>
      ))}
  </Box>
);

interface GetColumnsParams {
  onRequestClick: (requestId?: string) => void;
  openByContractNumber: Record<string, boolean>;
}

export const getServiceRequestsDashboardColumns = ({
  onRequestClick,
  openByContractNumber,
}: GetColumnsParams): GridColDef<ServiceRequestDashboardRow>[] => [
  {
    field: 'contractNumber',
    headerName: 'Номер договора',
    flex: 1,
    renderCell: ({row}) =>
      renderParentColumn(renderText(row.contractNumber), row.serviceRequests.length, Boolean(openByContractNumber[row.contractRowId])),
  },
  {
    field: 'startDate',
    headerName: 'Дата',
    flex: 1,
    renderCell: ({row}) =>
      renderParentColumn(
        row.startDate ? renderText(dayjs(row.startDate).format(DATE_FORMAT)) : null,
        row.serviceRequests.length,
        Boolean(openByContractNumber[row.contractRowId]),
      ),
  },
  {
    field: 'companyName',
    headerName: 'Заказчик',
    flex: 1,
    renderCell: ({row}) =>
      renderParentColumn(renderText(row.companyName), row.serviceRequests.length, Boolean(openByContractNumber[row.contractRowId])),
  },
  {
    field: 'serviceType',
    headerName: 'Вид работ',
    flex: 1,
    renderCell: ({row}) =>
      renderParentColumn(renderText(row.serviceType), row.serviceRequests.length, Boolean(openByContractNumber[row.contractRowId])),
  },
  {
    field: 'id',
    headerName: 'Заявка',
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      renderChildrenColumn(
        row.serviceRequests,
        Boolean(openByContractNumber[row.contractRowId]),
        (child) =>
          child.requestId ? (
            <ButtonBase
              onClick={(event) => {
                event.stopPropagation();
                onRequestClick(child.requestId);
              }}
              sx={{
                ...textStyles,
                color: '#1976d2',
                textDecoration: 'underline',
                justifyContent: 'flex-start',
              }}
            >
              {child.requestId}
            </ButtonBase>
          ) : null,
      ),
  },
  {
    field: 'fullAddress',
    headerName: 'Адрес заявки',
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      renderChildrenColumn(row.serviceRequests, Boolean(openByContractNumber[row.contractRowId]), (child) =>
        renderText(child.fullAddress),
      ),
  },
  {
    field: 'customer',
    headerName: 'Абонент',
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      renderChildrenColumn(row.serviceRequests, Boolean(openByContractNumber[row.contractRowId]), (child) =>
        renderText(child.customer),
      ),
  },
  {
    field: 'workers',
    headerName: 'Исполнитель',
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      renderChildrenColumn(row.serviceRequests, Boolean(openByContractNumber[row.contractRowId]), (child) =>
        renderText(child.workers),
      ),
  },
  {
    field: 'plannedDate',
    headerName: 'Дата окончания',
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      renderChildrenColumn(row.serviceRequests, Boolean(openByContractNumber[row.contractRowId]), (child) =>
        child.plannedDate ? renderText(dayjs(child.plannedDate).format(DATE_FORMAT)) : null,
      ),
  },
  {
    field: 'statusByDate',
    headerName: 'Статус',
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      renderChildrenColumn(
        row.serviceRequests,
        Boolean(openByContractNumber[row.contractRowId]),
        (child) =>
          child.statusByDate ? (
            <StatusColorCell>
              <ServiceRequestStatusColorCircle status={child.statusByDate} archived={child.archived} />
            </StatusColorCell>
          ) : null,
        () =>
          row.statusByDate ? (
            <StatusColorCell>
              <ServiceRequestStatusColorCircle status={row.statusByDate} archived={row.archived} />
            </StatusColorCell>
          ) : null,
      ),
  },
];
